import { StrictMode } from 'react'
import ReactDOM from 'react-dom/client'
import { PreviewPasswordGate } from './components/PreviewPasswordGate'
import { initSentry } from './lib/sentry'
import { isChunkLoadError } from './lib/importOnDemand'
import { isAuthDisabled } from './lib/authMode'
import './index.css'

initSentry()

// Client Portal v2 is the same repo built as a SECOND Vercel project with
// VITE_APP_SURFACE=portal. It mounts its own router and never the agency app,
// so the client bundle carries no agency routes (see src/portal/PortalApp.tsx).
const isPortalSurface = import.meta.env.VITE_APP_SURFACE === 'portal'

const previewGated = isAuthDisabled()

const root = ReactDOM.createRoot(document.getElementById('root')!)

// The surface import below is the ROOT chunk load, so it needs the same
// stale-chunk handling lazyWithRetry gives every route — without it a failed
// load was a blank white page (BLOODSTREAM-FRONTEND-K / -1J / -12: "Failed to
// fetch dynamically imported module …/App-<hash>.js" and "'text/html' is not a
// valid JavaScript MIME type"). A chunk failure reloads ONCE to pick up the
// current build; Chromium caches a failed module fetch in the document's module
// map, so retrying in place cannot help (see importOnDemand.ts). The timestamp
// guard stops a genuinely broken deploy from reload-looping: the second failure
// inside a minute renders a refresh prompt instead and reaches Sentry.
const ROOT_RELOAD_KEY = 'bs_root_chunk_reload_at'
const ROOT_RELOAD_WINDOW_MS = 60_000

function recoverRootLoad(err: unknown): void {
  let lastReload = 0
  try {
    lastReload = Number(sessionStorage.getItem(ROOT_RELOAD_KEY)) || 0
  } catch {
    // Storage blocked: fall through to the prompt rather than risk a loop.
    lastReload = Date.now()
  }
  if (isChunkLoadError(err) && Date.now() - lastReload > ROOT_RELOAD_WINDOW_MS) {
    try {
      sessionStorage.setItem(ROOT_RELOAD_KEY, String(Date.now()))
    } catch {
      // Unreachable in practice: the read above already proved storage works.
    }
    window.location.reload()
    return
  }
  root.render(
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center">
      <p className="text-base text-muted">Bloodstream couldn&rsquo;t finish loading.</p>
      <button type="button" className="btn-primary" onClick={() => window.location.reload()}>
        Refresh the page
      </button>
    </div>,
  )
  // Rethrow so the global unhandledrejection handler still reports it.
  throw err
}

// Each surface is a DYNAMIC import behind a build-time constant, so Rollup
// drops the other surface's whole module graph — the portal bundle never
// contains src/lib/supabase.ts (scripts/check-portal-bundle.mjs proves it).
if (isPortalSurface) {
  import('./portal/PortalApp').then(({ PortalApp }) => {
    root.render(<StrictMode><PortalApp /></StrictMode>)
  }, recoverRootLoad)
} else {
  import('./App.tsx').then(({ default: App }) => {
    root.render(
      <StrictMode>
        {previewGated ? (
          <PreviewPasswordGate>
            <App />
          </PreviewPasswordGate>
        ) : (
          <App />
        )}
      </StrictMode>,
    )
  }, recoverRootLoad)
}
